import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowLeft, Users, Truck, Car, Route, DollarSign, AlertTriangle, Activity, ShieldAlert, Mail, Phone, ArrowLeftRight, Send } from "lucide-react";
import { KpiCard, Loading } from "@/components/portal/widgets";
import { ChartPanel, StatusBadge, tooltipStyle, ErrorNote } from "@/components/founder/ui";
import { PlanDialog, WinbackDialog } from "@/components/founder/CompanyDialogs";
import { CompanyNotes } from "@/components/founder/CompanyNotes";
import { useFounder, money, fmtD, fmtMonth, daysAgo } from "@/lib/founderApi";

const Row = ({ k, v, t }) => (<div className="flex justify-between gap-4 py-1.5 text-sm"><dt className="text-slate-500">{k}</dt><dd className="text-right text-slate-200" data-testid={t}>{v}</dd></div>);

export default function FounderCompany() {
  const { orgId } = useParams();
  const nav = useNavigate();
  const { data: c, error, reload } = useFounder(`/platform/companies/${orgId}`);
  const { data: billing, reload: reloadBilling } = useFounder(`/companies/${orgId}/billing`);
  const [planOpen, setPlanOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  if (error) return <ErrorNote error={error} />;
  if (!c) return <Loading label="Loading company…" />;
  const u = c.usage || {};
  const idle = daysAgo(c.last_active);

  return (
    <div className="space-y-6" data-testid="founder-company">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="company-back"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-3"><h1 className="font-display text-2xl font-bold" data-testid="company-name">{c.name}</h1><StatusBadge value={c.status} testid="company-status" /></div>
          <p className="mt-1 text-sm text-slate-400">{c.region || "No address on file"} · joined {fmtD(c.created_at)} · {idle == null ? "never active" : idle === 0 ? "active today" : `last active ${idle}d ago`}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMailOpen(true)} data-testid="company-email-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800"><Send className="h-4 w-4" /> Email owner</button>
          <button onClick={() => setPlanOpen(true)} data-testid="company-plan-btn" className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold hover:bg-blue-700"><ArrowLeftRight className="h-4 w-4" /> Change plan</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard icon={Truck} label="Drivers" value={u.drivers ?? 0} sub={`${u.active_drivers_30d ?? 0} active in 30 days`} testid="company-kpi-drivers" />
        <KpiCard icon={Car} label="Vehicles" accent="slate" value={u.vehicles ?? 0} testid="company-kpi-vehicles" />
        <KpiCard icon={Users} label="Team" accent="slate" value={u.users ?? 0} sub={Object.entries(c.team || {}).map(([r, n]) => `${n} ${r}`).join(" · ")} testid="company-kpi-users" />
        <KpiCard icon={Route} label="Trips (30d)" value={u.trips_30d ?? 0} sub={`${u.trips ?? 0} all-time · ${u.incidents ?? 0} incidents`} testid="company-kpi-trips" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <ChartPanel title="Subscription" testid="company-subscription">
          <dl>
            <Row k="Plan" v={<span><span className="capitalize">{c.plan}</span> · {money(c.plan_price)}/{c.billing_cycle === "year" ? "yr" : "mo"}</span>} t="company-plan" />
            <Row k="MRR" v={money(c.mrr)} />
            {c.trial_ends_at && <Row k="Trial ends" v={fmtD(c.trial_ends_at)} />}
            {c.current_period_end && <Row k="Renews" v={fmtD(c.current_period_end)} />}
            <Row k="Stripe" v={c.stripe_status || (billing?.has_stripe ? billing.status : "No subscription yet")} />
            {billing?.manual_plan && <Row k="Manual override" v={<span className="text-amber-300" data-testid="company-override"><span className="capitalize">{billing.manual_plan}</span> (billing unchanged)</span>} />}
          </dl>
          {billing?.audit?.length > 0 && (
            <div className="mt-3 border-t border-slate-800 pt-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Your plan changes</p>
              {billing.audit.slice(0, 5).map((a) => <p key={a.at} className="text-xs text-slate-400">{fmtD(a.at)} · <span className="capitalize">{a.plan}</span> · {a.billing === "stripe" ? "with Stripe" : "access only"}</p>)}
            </div>
          )}
        </ChartPanel>
        <ChartPanel title="Owner" testid="company-owner">
          <p className="font-semibold text-white">{c.owner?.name}</p>
          {c.owner?.email && <a href={`mailto:${c.owner.email}`} className="mt-2 flex items-center gap-2 text-sm text-blue-300 hover:underline" data-testid="company-owner-email"><Mail className="h-4 w-4" /> {c.owner.email}</a>}
          {c.owner?.phone && <a href={`tel:${c.owner.phone}`} className="mt-1 flex items-center gap-2 text-sm text-blue-300 hover:underline" data-testid="company-owner-phone"><Phone className="h-4 w-4" /> {c.owner.phone}</a>}
          <dl className="mt-3 border-t border-slate-800 pt-2">
            <Row k="Revenue to date" v={money(c.revenue_total)} />
            <Row k="Failed payments" v={<span className={c.failed_payments ? "text-red-300" : ""}>{c.failed_payments}</span>} />
            <Row k="Time zone" v={c.timezone || "—"} />
          </dl>
        </ChartPanel>
        <ChartPanel title="Trips per month" hint="How much they use RideStamp" testid="company-trips-chart">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={c.trips_monthly || []}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} width={36} allowDecimals={false} />
              <Tooltip {...tooltipStyle} labelFormatter={fmtMonth} formatter={(v) => [v, "Trips"]} />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartPanel title="Payments" testid="company-payments">
          {!(c.payments || []).length ? <p className="text-sm text-slate-500">No payments recorded yet.</p> : (
            <ul className="divide-y divide-slate-800">
              {c.payments.map((p, i) => (
                <li key={p.id || i} className="flex items-center justify-between py-2 text-sm" data-testid={`company-payment-${i}`}>
                  <span className="text-slate-400">{fmtD(p.created_at || p.date || p.paid_at)}</span>
                  <span className="flex items-center gap-2">{p.status && p.status !== "paid" && <AlertTriangle className="h-3.5 w-3.5 text-red-300" />}<span className="font-mono-data text-emerald-300">{money(p.amount)}</span><span className="text-xs capitalize text-slate-500">{p.status}</span></span>
                </li>
              ))}
            </ul>
          )}
        </ChartPanel>
        <CompanyNotes orgId={orgId} />
      </div>
      <p className="flex items-center gap-1.5 text-xs text-slate-600"><Activity className="h-3.5 w-3.5" /> Org ID {c.id}{c.stripe_customer_id ? ` · Stripe ${c.stripe_customer_id}` : ""} <DollarSign className="ml-2 h-3.5 w-3.5" /><ShieldAlert className="h-3.5 w-3.5" /> Read from the live RideStamp system</p>

      <PlanDialog company={c} billing={billing} open={planOpen} onOpenChange={setPlanOpen} onDone={() => { reload(); reloadBilling(); }} />
      <WinbackDialog company={c} open={mailOpen} onOpenChange={setMailOpen} />
    </div>
  );
}
